import { Resume } from '@rio/types'
import { useDispatch } from 'react-redux'
import { DocumentTypes } from 'builder/modules/constants'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useTemplateCustomization } from 'builder/components/DocumentCustomizationModal'
import PreviewMobileContent from 'builder/components/DocumentCustomizationModal/components/MobileDocumentCustomizationModal/PreviewContent'

import { actions } from 'builder/modules/resumeEditor'
import { useI18n } from 'builder/hooks/useI18n'
import { usePreviewResume } from './hooks/usePreviewResume'
import * as Styles from './styles'

type Props = {
  resume: Resume
  hideCustomizeButton?: boolean
}

export const PreviewResume = (props: Props) => {
  const { resume, hideCustomizeButton = false } = props
  const dispatch = useDispatch()
  const { isPhone, isTablet } = useMediaQueries()
  const { currentTemplate } = useTemplateCustomization(resume)
  const editorState = useTypedSelector(state => state.generalEditor)
  const isDataLoaded = !!resume && editorState.isLoaded
  const { i18n } = useI18n()

  const {
    dimensions,
    contentRef,
    previewRef,
    previewBoxRef,
    totalPageNumber,
    currentPageNumber,
    handlePreviewResize,
    handleNextPageClick,
    handlePreviousPageClick,
    handlePaginationStateChange,
  } = usePreviewResume(resume)

  const switchToCustomizationTab = () => {
    dispatch(actions.setEditorPanelType('customization'))
  }

  if (isPhone || isTablet) {
    return (
      <PreviewMobileContent
        {...{
          ...dimensions,
          contentRef,
          previewBoxRef,
          previewRef,
          type: DocumentTypes.resume,
          isDataLoaded,
          currentDocument: resume,
          currentTemplate,
          currentPage: currentPageNumber,
          totalPagesNumber: totalPageNumber,
          handlePreviousPageClick,
          handleNextPageClick,
          handlePaginationStateChange,
          handlePreviewResize,
          applyBodyScrollLock: false,
          style: {
            zIndex: 5,
            height: '100%',
            width: '100%',
            paddingBottom: '20px',
          },
        }}
      />
    )
  }

  return (
    <Styles.Container>
      <Styles.ScrollContainer>
        <Styles.InnerContainer ref={previewBoxRef}>
          <Styles.StyledPreview
            ref={previewRef}
            {...dimensions}
            document={resume}
            documentId={resume.id}
            type={DocumentTypes.resume}
            onPaginationChange={handlePaginationStateChange}
            isExperimentalFastRenderingEnabled
          />
        </Styles.InnerContainer>
        {!hideCustomizeButton && (
          <Styles.CustomizeContainer onClick={switchToCustomizationTab}>
            <Styles.CustomizeButton leadingIcon={<Styles.CustomizeIcon />}>
              <Styles.AnimatedText>
                {i18n.t('builder.resume_editor.change_design')}
              </Styles.AnimatedText>
            </Styles.CustomizeButton>
          </Styles.CustomizeContainer>
        )}
        {!!totalPageNumber && (
          <Styles.PagesContainer>
            <Styles.IconsButton
              aria-label="Previous page"
              onClick={handlePreviousPageClick}
              disabled={currentPageNumber === 1}
            >
              <Styles.LeftIcon />
            </Styles.IconsButton>
            <Styles.Page>
              {currentPageNumber} / {totalPageNumber}
            </Styles.Page>
            <Styles.IconsButton
              aria-label="Next page"
              onClick={handleNextPageClick}
              disabled={currentPageNumber === totalPageNumber}
            >
              <Styles.RightIcon />
            </Styles.IconsButton>
          </Styles.PagesContainer>
        )}
      </Styles.ScrollContainer>
    </Styles.Container>
  )
}
